// Catálogo de roles para la UI: etiquetas legibles (en español) y qué roles se
// pueden ASIGNAR al crear/editar un usuario. Los valores técnicos (enum RolPerfil)
// siguen yendo a la BD tal cual; aquí solo se controla su presentación.

// Etiqueta legible por rol (cubre TODOS los valores del enum, incluidos los que ya
// no se asignan, para mostrarlos bien si un usuario existente los tiene).
export const ROLES_LABEL: Record<string, string> = {
  DIRECTOR_INSTRUCCION: 'Director de Instrucción',
  DIRECTOR_JUZGAMIENTO: 'Director de Juzgamiento',
  DIRECTOR_SEGUNDA_INSTANCIA: 'Director de Segunda Instancia',
  // Nombre dictado por el cliente (ago-06); el enum de BD no cambia.
  FUNCIONARIO_INSTRUCCION: 'Funcionario abogado sustanciador – Instrucción',
  // Nombre dictado por el cliente (ago-05); el enum de BD no cambia.
  CONTRATISTA_INSTRUCCION: 'Contratista abogado sustanciador – Instrucción',
  FUNCIONARIO_JUZGAMIENTO: 'Funcionario de Juzgamiento',
  CONTRATISTA_JUZGAMIENTO: 'Contratista de Juzgamiento',
  TECNICO_ADMINISTRATIVO: 'Técnico administrativo',
  // Rol nuevo (ago-06, dictado por el cliente): igual que el técnico
  // administrativo EXCEPTO crear/editar usuarios.
  APOYO_TECNICO_ADMINISTRATIVO: 'Apoyo técnico administrativo – Instrucción',
  TECNOLOGO_APOYO: 'Tecnólogo de apoyo',
  // Rol nuevo (ago-06, dictado por el cliente): revisa, aprueba (firma) o
  // devuelve con comentarios las decisiones donde esté añadido como revisor.
  ABOGADO_ESPECIALISTA: 'Abogado especialista – Instrucción',
  SECRETARIA: 'Secretaría',
  SECRETARIA_JURIDICA: 'Secretaría Jurídica',
  SEGUNDA_INSTANCIA: 'Segunda instancia (Gobernador)',
  SUPERVISOR: 'Supervisor',
  // Roles nuevos de la Orden 1 (aún no en el enum de BD; ver permisos.ts backend).
  APOYO_SUPERVISOR: 'Apoyo al supervisor',
  ADMINISTRATIVO_CONSOLIDACION: 'Administrativo (consolidación)',
  MINISTERIO_PUBLICO: 'Ministerio Público',
  VICTIMA: 'Víctima',
  DEFENSOR: 'Defensor',
  DISCIPLINADO: 'Disciplinado',
  QUEJOSO: 'Quejoso',
  SUPER_USUARIO: 'Superusuario',
  ADMINISTRADOR: 'Administrador',
};

/**
 * ABOGADOS SUSTANCIADORES de Instrucción: la LISTA que se ofrece para elegir
 * quién revisa/firma el acta de reparto y quién revisa la decisión inicial
 * (decidido con el cliente ago-06: ya no se fija un rol único —el extinto
 * «Abogado sustanciador»/`ABOGADO_ESPECIALIZADO`—, se elige de la lista).
 */
export const ROLES_ABOGADOS_SUSTANCIADORES = [
  'CONTRATISTA_INSTRUCCION',
  'FUNCIONARIO_INSTRUCCION',
  'ABOGADO_ESPECIALISTA',
];

/** ¿El rol es de un abogado sustanciador (puede revisar/firmar el acta)? */
export function esAbogadoSustanciador(rol?: string | null): boolean {
  return !!rol && ROLES_ABOGADOS_SUSTANCIADORES.includes(rol);
}

/**
 * ABOGADOS a los que el Técnico administrativo puede ASIGNAR el proceso en
 * «Casos aprobados» (tras la firma del acta de reparto).
 *
 * Regla del cliente: **el que inicia la Etapa 2 es la persona elegida ahí**, sea
 * cual sea su rol. Esta constante alimenta el selector de la asignación Y los
 * permisos de la Etapa 2 (espejo de `RECEPTORES_CASO` en el backend), para que
 * nadie quede asignado a un caso que después no pueda trabajar.
 */
export const ROLES_RECEPTOR_CASO = [
  'CONTRATISTA_INSTRUCCION',
  'FUNCIONARIO_INSTRUCCION',
];

/** ¿El rol puede recibir el caso asignado (e iniciar la Etapa 2)? */
export function esReceptorCaso(rol?: string | null): boolean {
  return !!rol && ROLES_RECEPTOR_CASO.includes(rol);
}

/** Quienes DILIGENCIAN la Etapa 2: el abogado que recibió el caso + el tecnólogo. */
export const ROLES_FILLER_ETAPA2 = [...ROLES_RECEPTOR_CASO, 'TECNOLOGO_APOYO'];

/** ¿El rol diligencia la Etapa 2 (ve sus casos asignados en Control de ingreso)? */
export function esFillerEtapa2(rol?: string | null): boolean {
  return !!rol && ROLES_FILLER_ETAPA2.includes(rol);
}

/** Convierte un valor de enum en etiqueta legible (con respaldo genérico). */
export function labelRol(v?: string | null): string {
  if (!v) return '—';
  if (ROLES_LABEL[v]) return ROLES_LABEL[v];
  // Respaldo: MAYUS_CON_GUION → "Mayus con guion"
  const s = v.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Roles que un administrador PUEDE asignar al crear/editar un usuario.
// (Quejoso, Disciplinado y Defensor nacen en el portal, no se crean aquí;
//  Secretaría y Director de Segunda Instancia se retiraron del modelo.)
export const ROLES_SELECCIONABLES: string[] = [
  'DIRECTOR_INSTRUCCION',
  'DIRECTOR_JUZGAMIENTO',
  'FUNCIONARIO_INSTRUCCION',
  'CONTRATISTA_INSTRUCCION',
  'FUNCIONARIO_JUZGAMIENTO',
  'CONTRATISTA_JUZGAMIENTO',
  'TECNICO_ADMINISTRATIVO',
  'APOYO_TECNICO_ADMINISTRATIVO',
  'TECNOLOGO_APOYO',
  'ABOGADO_ESPECIALISTA',
  'SECRETARIA_JURIDICA',
  'SEGUNDA_INSTANCIA',
  'SUPERVISOR',
  'ADMINISTRADOR',
];

// Roles asignables SEGÚN QUIÉN crea (decidido con el cliente): el Director de
// Instrucción solo crea el personal de su dependencia, con las etiquetas que él
// maneja.
// Personal de Instrucción que ofrecen los creadores de esa dependencia (el
// Director y ahora también el Técnico administrativo, que puede crear/editar
// usuarios — pedido del cliente ago-06; su lista es la misma, POR VALIDAR).
const PERSONAL_INSTRUCCION: Array<{ v: string; label: string }> = [
  { v: 'SUPERVISOR', label: 'Supervisor – Instrucción' },
  { v: 'ABOGADO_ESPECIALISTA', label: 'Abogado especialista – Instrucción' },
  { v: 'TECNOLOGO_APOYO', label: 'Tecnólogo de apoyo – Instrucción' },
  { v: 'TECNICO_ADMINISTRATIVO', label: 'Técnico administrativo – Instrucción' },
  { v: 'APOYO_TECNICO_ADMINISTRATIVO', label: 'Apoyo técnico administrativo – Instrucción' },
  { v: 'CONTRATISTA_INSTRUCCION', label: 'Contratista abogado sustanciador – Instrucción' },
  { v: 'FUNCIONARIO_INSTRUCCION', label: 'Funcionario abogado sustanciador – Instrucción' },
];

const ROLES_POR_CREADOR: Record<string, Array<{ v: string; label: string }>> = {
  DIRECTOR_INSTRUCCION: PERSONAL_INSTRUCCION,
  TECNICO_ADMINISTRATIVO: PERSONAL_INSTRUCCION,
};

/**
 * Opciones de rol (valor + etiqueta) que puede asignar quien está creando el
 * usuario. Los creadores sin lista propia (otros directores, superusuario) ven
 * el catálogo completo de ROLES_SELECCIONABLES. El rol SUPER_USUARIO solo lo
 * ofrece (y puede asignar) el ADMINISTRADOR — nadie más crea superusuarios.
 */
export function rolesAsignablesPor(rolCreador?: string | null): Array<{ v: string; label: string }> {
  if (rolCreador === 'ADMINISTRADOR') {
    return [...ROLES_SELECCIONABLES, 'SUPER_USUARIO'].map((r) => ({ v: r, label: labelRol(r) }));
  }
  return (
    ROLES_POR_CREADOR[rolCreador ?? ''] ??
    ROLES_SELECCIONABLES.map((r) => ({ v: r, label: labelRol(r) }))
  );
}
